// src/pages/income/service/IncomeServiceList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./IncomeServiceList.module.css";
import api from "../../../api";
import * as XLSX from "xlsx";
import useHighlightFromQuery from "../../../hooks/useHighlightFromQuery";

const DEBUG = false;
const CATEGORY = "용역사업"; // ✅ 용역사업 고정

export default function IncomeServiceList() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ 페이지 제목
  useEffect(() => {
    document.title = "용역사업 수입목록";
    if (!location.state?.tabTitle) {
      navigate({ pathname: ".", search: location.search }, {
        replace: true,
        state: { ...(location.state || {}), tabTitle: "용역사업 수입목록" },
      });
    }
  }, []); // eslint-disable-line

  // ✅ 날짜 기본값
  const year = new Date().getFullYear();
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const [fromDate, setFromDate] = useState(yearStart);
  const [toDate, setToDate] = useState(yearEnd);

  // ✅ 검색 상태
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // ✅ 세부구분 필터
  const [subcategory, setSubcategory] = useState("");

  // ✅ 귀속월 필터
  const [workMonthFilter, setWorkMonthFilter] = useState("");


  // ✅ 거래처 자동완성
  const [partnerInput, setPartnerInput] = useState("");
  const [partnerId, setPartnerId] = useState(null);
  const [partners, setPartners] = useState([]);
  const [showPartnerList, setShowPartnerList] = useState(false);
  const partnerWrapRef = useRef(null);

  // ✅ 목록 데이터

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    draft_date: "", draft_no: "", title: "",
    amount: "", pay_method: "transfer",
    partner_id: null, partner_name: "",
  });
  const [editPartnerInput, setEditPartnerInput] = useState("");
  const [editPartners, setEditPartners] = useState([]);
  const [showEditPartnerList, setShowEditPartnerList] = useState(false);
  const editPartnerWrapRef = useRef(null);

  // ✅ 거래처 자동완성
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (!partnerInput?.trim()) {
          if (!ignore) setPartners([]);
          return;
        }
        const { data } = await api.get("/vendors/", { params: { q: partnerInput } });


        let items = [];
        if (Array.isArray(data)) items = data;
        else if (Array.isArray(data?.results)) items = data.results;
        else if (Array.isArray(data?.data)) items = data.data;
        if (!ignore) setPartners(items);
      } catch (e) {}
    })();
    return () => {
      ignore = true;
    };
  }, [partnerInput]);

  // ✅ 목록 조회 (용역사업 데이터 불러오기)
  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = {
          category: CATEGORY,
          subcategory: subcategory || undefined,
          partner: partnerId || undefined,
          search: search || undefined,
          ordering: "-draft_date,-seq",
        };

        const res = await api.get("/income-service/", { params });

        const data = res?.data;
        const list = Array.isArray(data) ? data : data?.results ?? [];
        if (!ignore) setRows(list);
      } catch (e) {
        if (!ignore)
          setError(e?.response?.data?.detail || e?.message || "불러오기 실패");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [search, subcategory]);

  // ✅ 기간 필터 + 합계 계산
  const filtered = useMemo(() => {
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    if (to) to.setHours(23, 59, 59, 999);
    
    return rows
    .filter((r) => {
      // ✅ 귀속월 필터
      if (workMonthFilter && r.work_month !== workMonthFilter) return false;
      const d = r?.draft_date ? new Date(r.draft_date) : null;
      if (!d) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    })
    .sort((a, b) => {
      // 날짜 내림차순 (최신이 위)
      if (a.draft_date > b.draft_date) return -1;
      if (a.draft_date < b.draft_date) return 1;
      const getNo = (r) => {
        const m = r.draft_no?.match(/-(\d+)호$/);
        return m ? parseInt(m[1], 10) : (r.seq ?? 0);
      };
      return getNo(b) - getNo(a);
    });
  }, [rows, fromDate, toDate, workMonthFilter]);   // ✅ 의존성 추가



  const totalAmount = useMemo(
    () => filtered
      .filter(r => r.draft_kind !== "official")
      .reduce((a, r) => a + (Number(r?.amount) || 0), 0),
    [filtered]
  );

  // ✅ 엑셀 다운로드
  const downloadExcel = () => {
    if (!filtered.length) return alert("다운로드할 데이터가 없습니다.");
  
    const toN = (v) => Number(String(v || "0").replace(/,/g, "")) || 0;
  
    const rows = filtered.filter(r => r.draft_kind !== "official").map((r) => {
      const amt = toN(r.amount);
      return {
        귀속월: r.work_month || "",
        번호: r.draft_no || String(r.seq ?? "").padStart(3, "0"),
        거래처: r?.partner?.name ?? r?.partner_name ?? "",
        금액: amt,
        계좌이체: r.pay_method === "transfer" ? amt : 0,
        현금결제: r.pay_method === "cash" ? amt : 0,
        카드결제: r.pay_method === "card" ? amt : 0,
        제목: r.title || "",
        비고: r.desc || "",
      };
    });
  
    // 합계행
    const sumRow = {
      귀속월: "합계",
      번호: "",
      거래처: "",
      금액: rows.reduce((a, r) => a + r.금액, 0),
      계좌이체: rows.reduce((a, r) => a + r.계좌이체, 0),
      현금결제: rows.reduce((a, r) => a + r.현금결제, 0),
      카드결제: rows.reduce((a, r) => a + r.카드결제, 0),
      제목: "",
      비고: "",
    };
  
    const ws = XLSX.utils.json_to_sheet([sumRow, ...rows]);
  
    // 컬럼 너비 살짝 조정 (선택)
    ws["!cols"] = [
      { wch: 10 }, // 귀속월
      { wch: 28 }, // 번호
      { wch: 20 }, // 거래처
      { wch: 12 }, // 금액
      { wch: 12 }, // 계좌이체
      { wch: 12 }, // 현금결제
      { wch: 12 }, // 카드결제
      { wch: 40 }, // 제목
      { wch: 30 }, // 비고
    ];
  
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "용역사업수입목록");
  
    const filename = workMonthFilter
      ? `용역사업_수입목록_${workMonthFilter}.xlsx`
      : `용역사업_수입목록_${fromDate}_${toDate}.xlsx`;
    XLSX.writeFile(wb, filename);
  };


  // ✅ 수정 시작
  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({
      draft_date: r.draft_date || "",
      draft_no: r.draft_no || "",
      title: r.title || "",
      amount: String(r.amount || ""),
      pay_method: r.pay_method || "transfer",
      partner_id: r.partner?.id ?? r.partner_id ?? null,
      partner_name: r.partner?.name ?? r.partner_name ?? "",
      work_month: r.work_month || "",   // ✅ 추가


    });
    setEditPartnerInput(r.partner?.name ?? r.partner_name ?? "");
  };

  // ✅ 원본 데이터 열기(?highlight=id) — 대상 행을 찾아 스크롤+하이라이트, 인라인 수정 모드까지 자동 진입
  const isHighlighted = useHighlightFromQuery(filtered, { setFromDate, setToDate, onFound: startEdit });

  // ✅ 수정 취소
  const cancelEdit = () => {
    setEditingId(null);
    setEditPartnerInput("");
  };
  
  // ✅ 수정 저장
  const saveEdit = async (r) => {
    if (editSaving) return;
    setEditSaving(true);
    try {
      const payload = {
        draft_date: editForm.draft_date,
        draft_no: editForm.draft_no,
        title: editForm.title,
        amount: Number(String(editForm.amount).replace(/,/g, "")),
        pay_method: editForm.pay_method,
        partner_id: editForm.partner_id,
        work_month: editForm.work_month,   // ✅ 추가

      };
      await api.patch(`/income-service/${r.id}/`, payload);
      const saved = { ...editForm };
      alert("✅ 수정 완료!");
      setEditingId(null);
      setEditPartnerInput("");
      setRows((prev) =>
        prev.map((row) =>
          row.id === r.id
            ? {
                ...row,
                draft_date: saved.draft_date,
                draft_no: saved.draft_no,
                title: saved.title,
                amount: payload.amount,
                pay_method: saved.pay_method,
                work_month: saved.work_month,   

                partner: { id: saved.partner_id, name: saved.partner_name },
              }
            : row
        )
      );
    } catch (err) {
      alert("수정 실패: " + (err?.response?.data?.detail || err.message));
    } finally {
      setEditSaving(false);
    }
  };
  
  // ✅ 삭제
  const deleteRow = async (r) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/income-service/${r.id}/`);
      alert("삭제되었습니다.");
      setRows((prev) => prev.filter((row) => row.id !== r.id));
    } catch (err) {
      alert("삭제 실패: " + (err?.response?.data?.detail || err.message));
    }
  };
  
  // ✅ 편집 중 거래처 자동완성
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (!editPartnerInput?.trim()) { if (!ignore) setEditPartners([]); return; }
        const { data } = await api.get("/vendors/", { params: { q: editPartnerInput } });

        let items = [];
        if (Array.isArray(data)) items = data;
        else if (Array.isArray(data?.results)) items = data.results;
        if (!ignore) setEditPartners(items);
      } catch (e) {}
    })();
    return () => { ignore = true; };
  }, [editPartnerInput]);
  
  if (loading) return <div style={{ padding: 16 }}>불러오는 중…</div>;
  if (error) return <div style={{ padding: 16, color: "red" }}>{error}</div>;


  return (
    <div style={{ padding: 16 }}>
      <h2>용역사업 수입목록</h2>

      {/* 필터 바 */}
      <div className={styles.filterBar}>
        {/* 기간 필터 */}
        <div className={styles.filterItem}>
          <div className={styles.label}>기간</div>
          <div className={styles.dateRow}>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={styles.inputBase}
            />
            <span className={styles.dateDash}>~</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={styles.inputBase}
            />
          </div>
        </div>

        {/* ✅ 세부구분 필터 */}
        <div className={styles.filterItem}>
          <div className={styles.label}>세부구분</div>
          <select
            value={subcategory}
            onChange={(e) => setSubcategory(e.target.value)}
            className={styles.inputBase}
          >
            <option value="">전체</option>
            <option value="용역">용역</option>
            <option value="기타">기타</option>
          </select>
        </div>

        {/* ✅ 귀속월 필터 */}
        <div className={styles.filterItem}>
          <div className={styles.label}>귀속월</div>
          <input
            type="month"
            value={workMonthFilter}
            onChange={(e) => setWorkMonthFilter(e.target.value)}
            className={styles.inputBase}
          />
        </div>

        {/* 검색 + 버튼 */}
        <div className={styles.filterItem}>


          <div className={styles.label}>검색</div>
          <div className={styles.searchRow}>
            <input
              className={`${styles.inputBase} ${styles.inputSearch}`}
              placeholder="거래처/기안번호/비고 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSearch(searchInput.trim());
              }}
            />
            <button
              type="button"
              className={styles.btn}
              onClick={() => setSearch(searchInput.trim())}
            >
              검색
            </button>
            <button
              type="button"
              className={styles.btn}

              onClick={() => {
                setFromDate(yearStart);
                setToDate(yearEnd);
                setSubcategory("");
                setWorkMonthFilter("");   // ✅ 추가
                setSearch("");
                setSearchInput("");
              }}


            >
              초기화
            </button>
            <button
              type="button"
              onClick={downloadExcel}
              className={styles.btnPrimary}
            >
              엑셀 다운로드
            </button>
          </div>
        </div>
      </div>

      {/* 테이블 */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th>귀속월</th>
            <th>번호</th>
            <th>거래처</th>
            <th>금액</th>
            <th className={styles.borderLeft}>계좌이체</th>
            <th>현금결제</th>
            <th>카드결제</th>
            <th className={styles.borderLeft}>제목</th>
            <th>비고</th>
          </tr>
        </thead>


        <tbody>
          {/* ✅ 합계 행 */}
          {filtered.length > 0 && (() => {
            const sums = { amount: 0, transfer: 0, cash: 0, card: 0 };

            filtered.forEach((r) => {
              // 공문(official)은 합계에서 제외 (내부기안과 금액 중복)
              if (r.draft_kind === "official") return;
              const amt = Number(r.amount) || 0;
              sums.amount += amt;
              if (r.pay_method === "transfer") sums.transfer += amt;
              if (r.pay_method === "cash") sums.cash += amt;
              if (r.pay_method === "card") sums.card += amt;
            });

            return (

              <tr
                style={{
                  background: "#f9fafb",
                  fontWeight: "bold",
                  borderBottom: "2px solid #d1d5db",
                }}
              >
                
                <td colSpan={3} style={{ textAlign: "center" }}>합계</td>
                <td style={{ textAlign: "right" }}>{sums.amount.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{sums.transfer.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{sums.cash.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{sums.card.toLocaleString()}</td>
                <td colSpan={2}></td>

              </tr>
            );
          })()}

          {/* ✅ 데이터 목록 */}
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={9}>데이터가 없습니다.</td>
            </tr>
          ) : (


            filtered.map((r) => {
              const isEditing = editingId === r.id;
              return (
                <tr
                  key={r.id}
                  id={`row-${r.id}`}
                  style={{
                    ...(isHighlighted(r.id) ? { background: "#fff59d", transition: "background 0.6s" } : {}),
                    ...(isEditing ? { background: "#eff6ff" } : {}),
                  }}
                >

                  {/* 귀속월 */}
                  <td>
                    {isEditing ? (
                      <input
                        type="month"
                        value={editForm.work_month}
                        onChange={(e) => setEditForm((p) => ({ ...p, work_month: e.target.value }))}
                        style={{ fontSize: "12px", border: "1px solid #60a5fa", borderRadius: "4px", width: "130px" }}
                      />
                    ) : r.work_month}
                  </td>

                  {/* 번호 */}
                  <td>
                    {isEditing ? (
                      <input type="text" value={editForm.draft_no}
                        onChange={(e) => setEditForm((p) => ({ ...p, draft_no: e.target.value }))}
                        style={{ fontSize: "12px", border: "1px solid #60a5fa", borderRadius: "4px", width: "140px" }}
                      />
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span>{r.draft_no || String(r.seq ?? "").padStart(3, "0")}</span>
                        {r.draft_kind === "internal" && (
                          <span style={{ fontSize: 10, background: "#dbeafe", color: "#1d4ed8", borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>내부기안</span>
                        )}
                        {r.draft_kind === "official" && (
                          <span style={{ fontSize: 10, background: "#fef9c3", color: "#854d0e", borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>공문</span>
                        )}
                      </div>
                    )}
                  </td>

            
                  {/* 거래처 */}
                  <td>
                    {isEditing ? (
                      <div ref={editPartnerWrapRef} style={{ position: "relative" }}>
                        <input type="text" value={editPartnerInput}
                          onChange={(e) => { setEditPartnerInput(e.target.value); setShowEditPartnerList(true); }}
                          style={{ fontSize: "12px", border: "1px solid #60a5fa", borderRadius: "4px", width: "120px" }}
                          placeholder="거래처 검색"
                        />
                        {showEditPartnerList && editPartners.length > 0 && (
                          <ul style={{
                            position: "absolute", zIndex: 999, background: "#fff",
                            border: "1px solid #d1d5db", borderRadius: "6px",
                            listStyle: "none", margin: 0, padding: "4px 0", width: "160px",
                          }}>
                            {editPartners.map((p) => (
                              <li key={p.id}
                                style={{ padding: "6px 12px", cursor: "pointer", fontSize: "13px" }}
                                onClick={() => {
                                  setEditForm((prev) => ({ ...prev, partner_id: p.id, partner_name: p.name }));
                                  setEditPartnerInput(p.name);
                                  setShowEditPartnerList(false);
                                }}
                              >{p.name}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : (r?.partner?.name ?? r?.partner_name ?? "")}
                  </td>
            
                  {/* 금액 */}
                  <td style={{ textAlign: "right" }}>
                    {isEditing ? (
                      <input type="text" value={editForm.amount}
                        onChange={(e) => {
                          const num = e.target.value.replace(/[^\d]/g, "");
                          setEditForm((p) => ({ ...p, amount: num }));
                        }}
                        style={{ fontSize: "12px", border: "1px solid #60a5fa", borderRadius: "4px", width: "90px", textAlign: "right" }}
                      />
                    ) : r.draft_kind === "official" ? (
                      <span style={{ color: "#9ca3af" }}>0</span>
                    ) : Number(r.amount).toLocaleString()}
                  </td>
            
                  {/* 계좌이체 */}
                  <td style={{ textAlign: "right" }}>
                    {isEditing ? (
                      <input type="text"
                        value={editForm.pay_method === "transfer" ? editForm.amount : ""}
                        onChange={(e) => {
                          const num = e.target.value.replace(/[^\d]/g, "");
                          setEditForm((p) => ({ ...p, amount: num, pay_method: "transfer" }));
                        }}
                        style={{ fontSize: "12px", border: "1px solid #60a5fa", borderRadius: "4px", width: "90px", textAlign: "right" }}
                      />
                    ) : r.draft_kind === "official" ? "" : (r.pay_method === "transfer" ? Number(r.amount).toLocaleString() : "")}
                  </td>
            
                  {/* 현금결제 */}
                  <td style={{ textAlign: "right" }}>
                    {isEditing ? (
                      <input type="text"
                        value={editForm.pay_method === "cash" ? editForm.amount : ""}
                        onChange={(e) => {
                          const num = e.target.value.replace(/[^\d]/g, "");
                          setEditForm((p) => ({ ...p, amount: num, pay_method: "cash" }));
                        }}
                        style={{ fontSize: "12px", border: "1px solid #60a5fa", borderRadius: "4px", width: "90px", textAlign: "right" }}
                      />
                    ) : r.draft_kind === "official" ? "" : (r.pay_method === "cash" ? Number(r.amount).toLocaleString() : "")}
                  </td>
            
                  {/* 카드결제 */}
                  <td style={{ textAlign: "right" }}>
                    {isEditing ? (
                      <input type="text"
                        value={editForm.pay_method === "card" ? editForm.amount : ""}
                        onChange={(e) => {
                          const num = e.target.value.replace(/[^\d]/g, "");
                          setEditForm((p) => ({ ...p, amount: num, pay_method: "card" }));
                        }}
                        style={{ fontSize: "12px", border: "1px solid #60a5fa", borderRadius: "4px", width: "90px", textAlign: "right" }}
                      />
                    ) : r.draft_kind === "official" ? "" : (r.pay_method === "card" ? Number(r.amount).toLocaleString() : "")}
                  </td>


                  {/* 제목 */}
                  <td className={styles.borderLeft}>
                    {isEditing ? (
                      <input type="text" value={editForm.title}
                        onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                        style={{ fontSize: "12px", border: "1px solid #60a5fa", borderRadius: "4px", width: "200px" }}
                      />
                    ) : r.title}
                  </td>
            
                  {/* 관리 버튼 */}
                  <td>
                    {isEditing ? (
                      <div style={{ display: "flex", gap: "4px" }}>
                        <button className={styles.btnLink} onClick={() => saveEdit(r)}
                          disabled={editSaving} style={{ color: "#1a73e8" }}>
                          {editSaving ? "저장중" : "저장"}
                        </button>
                        <button className={styles.btnLink} onClick={cancelEdit}
                          style={{ color: "#6b7280" }}>취소</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: "4px" }}>
                        <button className={styles.btnLink} onClick={() => startEdit(r)}>수정</button>
                        <button className={styles.btnLink} onClick={() => deleteRow(r)}
                          style={{ color: "#dc2626" }}>삭제</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            }) 



          )}
        </tbody>
      </table>
    </div>
  );
}

/* ✅ 유틸 */
async function downloadBlob(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const ct =
    res.headers?.["content-type"] ||
    res.headers?.["Content-Type"] ||
    "";
  if (!ct.includes("application/vnd.openxmlformats")) {
    try {
      const text = await (res.data.text ? res.data.text() : "");
      throw {
        response: { status: res.status, data: text || "(unknown error)" },
      };
    } catch {
      throw new Error("엑셀 응답 형식이 올바르지 않습니다.");
    }
  }
  const blob = new Blob([res.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const urlObj = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = urlObj;
  a.download = filename || "용역사업_수입목록.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(urlObj);
}
